import { comparer, computed, observable, reaction, runInAction } from 'mobx'
import { getError, getValue, isPending } from 'mobx-async'

export const asyncComputed = <T>(fn: () => Promise<T>, config?: { initialLoading?: boolean }) => {
  const refreshId = observable.box(1)
  const value = observable.box<T | undefined>(undefined)
  const loading = observable.box(config?.initialLoading ?? false)
  const error = observable.box<Error | undefined>()

  const computedPromise = computed(
    () => {
      void refreshId.get()
      return fn()
    },
    { keepAlive: true },
  )

  const disposers = [
    reaction(
      () => [isPending(computedPromise), getError(computedPromise)],
      ([pending, _error]) => {
        if (pending || _error) return
        const next = getValue(computedPromise)

        if (comparer.structural(value.get(), next)) {
          return
        }

        runInAction(() => {
          value.set(next)
        })
      },
    ),

    reaction(
      () => isPending(computedPromise),
      value => loading.set(value),
    ),
    reaction(
      () => getError(computedPromise),
      value => error.set(value),
    ),
  ]

  return {
    get loading() {
      return loading.get()
    },
    get value() {
      return value.get()
    },
    get error() {
      return error.get()
    },
    reset() {
      runInAction(() => {
        value.set(undefined)
        loading.set(false)
        error.set(undefined)
      })
    },
    resetAndRefresh() {
      runInAction(() => {
        loading.set(true)
        value.set(undefined)
        error.set(undefined)
        refreshId.set(refreshId.get() + 1)
      })
    },
    silentRefresh() {
      fn()
        .then(resp => {
          runInAction(() => {
            value.set(resp)
          })
        })
        .catch(() => {
          // Silently ignore errors to keep previous data
        })
    },
    refresh() {
      runInAction(() => {
        refreshId.set(refreshId.get() + 1)
      })
    },
    dispose() {
      disposers.forEach(dispose => dispose())
    },
  }
}
